import React, { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
} from "@material-ui/core";
import { SupportCategory, useSupportCategories } from "./hook/useSupportCategories";
import { useSupportPriorities, SupportPriority } from "../SupportPriorities/hook/useSupportPriorities";
import {
	ActionRow,
	ActiveDot,
	BadgePill,
	CodeCell,
	EmptyRow,
	ErrorAlert,
	PageHeader,
	PageSubtitle,
	PageTitle,
	PageWrap,
	PillBtn,
	PillDivider,
	PillRow,
	SectionCard,
	SectionHeader,
	SectionTitle,
	SegmentedBtn,
	SegmentedWrap,
	StyledTable,
	TableWrap,
} from "./styled/SupportCategoriesStyled";
import SupportPrioritiesPage from "../SupportPriorities/SupportPrioritiesPage";
import PermissionGate from "../../controls/auth/PermissionGate";

const BLANK: Omit<SupportCategory, "id"> = {
	code: "",
	label: "",
	applicableTo: "both",
	isActive: true,
	sortOrder: 0,
	bookingContext: "with_booking",
	defaultPriority: null,
};

const APPLICABLE_COLORS: Record<string, string> = {
	customer: "#2563eb",
	station: "#7c3aed",
	both: "#0891b2",
};

export default function SupportCategoriesPage() {
	const { categories, loading, error, create, update, remove } = useSupportCategories();
	const { priorities } = useSupportPriorities();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<SupportCategory | null>(null);
	const [form, setForm] = useState<Omit<SupportCategory, "id">>(BLANK);
	const [saving, setSaving] = useState(false);
	const [formError, setFormError] = useState("");
	const [deleteTarget, setDeleteTarget] = useState<SupportCategory | null>(null);
	const [deleteForce, setDeleteForce] = useState(false);
	const [deleteError, setDeleteError] = useState("");
	const [deleting, setDeleting] = useState(false);

	const [topTab, setTopTab] = useState<"categories" | "priorities">("categories");
	const [roleFilter, setRoleFilter] = useState<"customer" | "station">("customer");
	const [contextFilter, setContextFilter] = useState<"with_booking" | "without_booking">("with_booking");

	const roleFilteredCats = categories.filter((c) => c.applicableTo === roleFilter || c.applicableTo === "both");
	const visibleCats =
		roleFilter === "station"
			? roleFilteredCats
			: roleFilteredCats.filter((c) => c.bookingContext === contextFilter);

	function openCreate() {
		setEditing(null);
		setForm({
			...BLANK,
			applicableTo: roleFilter,
			bookingContext: roleFilter === "customer" ? contextFilter : "with_booking",
		});
		setFormError("");
		setDialogOpen(true);
	}

	function openEdit(cat: SupportCategory) {
		setEditing(cat);
		setForm({
			code: cat.code,
			label: cat.label,
			applicableTo: cat.applicableTo,
			isActive: cat.isActive,
			sortOrder: cat.sortOrder,
			bookingContext: cat.bookingContext,
			defaultPriority: cat.defaultPriority,
		});
		setFormError("");
		setDialogOpen(true);
	}

	async function handleSave() {
		setFormError("");
		setSaving(true);
		try {
			if (editing) {
				await update(editing.id, {
					label: form.label,
					applicableTo: form.applicableTo,
					isActive: form.isActive,
					sortOrder: form.sortOrder,
					bookingContext: form.bookingContext,
					defaultPriority: form.defaultPriority,
				});
			} else {
				await create(form);
			}
			setDialogOpen(false);
		} catch (e: any) {
			setFormError(e?.message || "Save failed");
		} finally {
			setSaving(false);
		}
	}

	async function handleToggleActive(cat: SupportCategory) {
		await update(cat.id, { isActive: !cat.isActive });
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleting(true);
		setDeleteError("");
		try {
			await remove(deleteTarget.id, deleteForce);
			setDeleteTarget(null);
			setDeleteForce(false);
		} catch (e: any) {
			const msg = e?.response?.data?.message || e?.message || "Delete failed";
			if (e?.response?.status === 409) {
				setDeleteError(msg);
				setDeleteForce(true);
			} else {
				setDeleteError(msg);
			}
		} finally {
			setDeleting(false);
		}
	}

	return (
		<PageWrap>
			<PageHeader>
				<PageTitle>Support Configuration</PageTitle>
				<PageSubtitle>
					Manage ticket categories and priority levels for the customer & station help center.
				</PageSubtitle>
			</PageHeader>

			<SegmentedWrap>
				<SegmentedBtn
					active={topTab === "categories"}
					onClick={() => setTopTab("categories")}>
					Categories
				</SegmentedBtn>
				<SegmentedBtn
					active={topTab === "priorities"}
					onClick={() => setTopTab("priorities")}>
					Priorities
				</SegmentedBtn>
			</SegmentedWrap>

			{/* ─── PRIORITIES TAB ──────────────────────────────────── */}
			{topTab === "priorities" && <SupportPrioritiesPage embedded />}

			{/* ─── CATEGORIES TAB ─────────────────────────────────── */}
			{topTab === "categories" && (
				<>
					{error && <ErrorAlert>{error}</ErrorAlert>}

					<SectionCard>
						<SectionHeader>
							<PillRow>
								<PillBtn
									active={roleFilter === "customer"}
									onClick={() => {
										setRoleFilter("customer");
										setContextFilter("with_booking");
									}}>
									Customer
								</PillBtn>
								<PillBtn
									active={roleFilter === "station"}
									onClick={() => setRoleFilter("station")}>
									Station
								</PillBtn>
								{roleFilter === "customer" && (
									<>
										<PillDivider />
										<PillBtn
											active={contextFilter === "with_booking"}
											onClick={() => setContextFilter("with_booking")}>
											Existing Booking
										</PillBtn>
										<PillBtn
											active={contextFilter === "without_booking"}
											onClick={() => setContextFilter("without_booking")}>
											No Booking
										</PillBtn>
									</>
								)}
							</PillRow>
							<PermissionGate permission="tickets.assign">
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={openCreate}
									disabled={loading}
									style={{
										borderRadius: 8,
										textTransform: "none",
										fontWeight: 600,
										fontSize: "0.82rem",
									}}>
									+ New Category
								</Button>
							</PermissionGate>
						</SectionHeader>

						<TableWrap>
							<StyledTable>
								<thead>
									<tr>
										<th>Code</th>
										<th>Label</th>
										<th>Scope</th>
										<th>Priority</th>
										<th style={{ width: 60 }}>Order</th>
										<th style={{ width: 60 }}>Active</th>
										<th style={{ width: 100, textAlign: "right" }}>Actions</th>
									</tr>
								</thead>
								<tbody>
									{visibleCats.map((cat) => (
										<tr key={cat.id}>
											<td>
												<CodeCell>{cat.code}</CodeCell>
											</td>
											<td style={{ fontWeight: 500 }}>{cat.label}</td>
											<td>
												<BadgePill color={APPLICABLE_COLORS[cat.applicableTo]}>
													{cat.applicableTo}
												</BadgePill>
											</td>
											<td>
												{cat.defaultPriority ? (
													(() => {
														const p = priorities.find(
															(pr) => pr.code === cat.defaultPriority
														);
														return p ? (
															<BadgePill color={p.color || "#64748b"}>
																{p.label}
															</BadgePill>
														) : (
															<CodeCell>{cat.defaultPriority}</CodeCell>
														);
													})()
												) : (
													<span style={{ color: "#94a3b8", fontSize: "0.82rem" }}>—</span>
												)}
											</td>
											<td>{cat.sortOrder}</td>
											<td>
												<ActiveDot on={cat.isActive} />
											</td>
											<td style={{ textAlign: "right" }}>
												<PermissionGate permission="tickets.assign">
													<Button
														size="small"
														onClick={() => openEdit(cat)}
														style={{
															minWidth: 0,
															textTransform: "none",
															fontSize: "0.8rem",
														}}>
														Edit
													</Button>
													<Button
														size="small"
														style={{
															minWidth: 0,
															color: "#dc2626",
															textTransform: "none",
															fontSize: "0.8rem",
														}}
														onClick={() => {
															setDeleteTarget(cat);
															setDeleteError("");
															setDeleteForce(false);
														}}>
														Delete
													</Button>
												</PermissionGate>
											</td>
										</tr>
									))}
									{!loading && visibleCats.length === 0 && (
										<tr>
											<EmptyRow
												colSpan={7}
												as="td">
												{roleFilter === "customer"
													? contextFilter === "with_booking"
														? "No categories for Existing Booking."
														: "No categories for No Booking."
													: "No station categories."}
											</EmptyRow>
										</tr>
									)}
								</tbody>
							</StyledTable>
						</TableWrap>
					</SectionCard>

					{/* Create / Edit dialog */}
					<Dialog
						open={dialogOpen}
						onClose={() => setDialogOpen(false)}
						maxWidth="sm"
						fullWidth>
						<DialogTitle style={{ fontWeight: 600 }}>
							{editing ? "Edit Category" : "New Category"}
						</DialogTitle>
						<DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
							{formError && <ErrorAlert style={{ margin: 0 }}>{formError}</ErrorAlert>}
							{!editing && (
								<TextField
									label="Code (slug)"
									value={form.code}
									onChange={(e) => {
										const slug = e.target.value
											.toLowerCase()
											.replace(/[^a-z0-9_]/g, "_")
											.replace(/_+/g, "_")
											.slice(0, 32);
										setForm((f) => ({ ...f, code: slug }));
									}}
									helperText="Lowercase letters, numbers, underscores."
									size="small"
									variant="outlined"
								/>
							)}
							<TextField
								label="Label"
								value={form.label}
								onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
								required
								size="small"
								variant="outlined"
							/>
							<FormControl
								variant="outlined"
								size="small">
								<InputLabel>Applicable To</InputLabel>
								<Select
									value={form.applicableTo}
									onChange={(e) => setForm((f) => ({ ...f, applicableTo: e.target.value as any }))}
									label="Applicable To">
									<MenuItem value="customer">Customer</MenuItem>
									<MenuItem value="station">Station</MenuItem>
									<MenuItem value="both">Both</MenuItem>
								</Select>
							</FormControl>
							{editing && (
								<FormControl
									variant="outlined"
									size="small">
									<InputLabel>Booking Context</InputLabel>
									<Select
										value={form.bookingContext}
										onChange={(e) =>
											setForm((f) => ({ ...f, bookingContext: e.target.value as any }))
										}
										label="Booking Context">
										<MenuItem value="with_booking">Existing Booking</MenuItem>
										<MenuItem value="without_booking">No Booking Yet</MenuItem>
									</Select>
								</FormControl>
							)}
							<FormControl
								variant="outlined"
								size="small">
								<InputLabel>Default Priority</InputLabel>
								<Select
									value={form.defaultPriority ?? ""}
									onChange={(e) =>
										setForm((f) => ({ ...f, defaultPriority: (e.target.value as string) || null }))
									}
									label="Default Priority">
									<MenuItem value="">
										<em>None (use system default)</em>
									</MenuItem>
									{priorities
										.filter((p) => p.isActive)
										.map((p) => (
											<MenuItem
												key={p.code}
												value={p.code}>
												<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
													{p.color && (
														<span
															style={{
																display: "inline-block",
																width: 10,
																height: 10,
																borderRadius: "50%",
																background: p.color,
															}}
														/>
													)}
													{p.label}
												</span>
											</MenuItem>
										))}
								</Select>
							</FormControl>
							<TextField
								label="Sort Order"
								type="number"
								value={form.sortOrder}
								onChange={(e) =>
									setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))
								}
								size="small"
								variant="outlined"
							/>
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<Switch
									checked={form.isActive}
									color="primary"
									onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
								/>
								<span style={{ fontSize: "0.9rem" }}>Active</span>
							</div>
						</DialogContent>
						<DialogActions style={{ padding: "12px 24px" }}>
							<Button
								onClick={() => setDialogOpen(false)}
								disabled={saving}>
								Cancel
							</Button>
							<Button
								onClick={handleSave}
								color="primary"
								variant="contained"
								disabled={saving || !form.label}
								style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}>
								{saving ? "Saving..." : "Save"}
							</Button>
						</DialogActions>
					</Dialog>

					{/* Delete confirm dialog */}
					<Dialog
						open={!!deleteTarget}
						onClose={() => {
							setDeleteTarget(null);
							setDeleteForce(false);
						}}>
						<DialogTitle style={{ fontWeight: 600 }}>Delete "{deleteTarget?.label}"?</DialogTitle>
						<DialogContent>
							{deleteError ? (
								<div style={{ color: "#dc2626", fontSize: "0.9rem", lineHeight: 1.6 }}>
									{deleteError}
									<br />
									Click Delete again to force-delete.
								</div>
							) : (
								<div style={{ color: "#475569", lineHeight: 1.6 }}>
									This will permanently remove the category. Existing tickets keep their category
									code.
								</div>
							)}
						</DialogContent>
						<DialogActions style={{ padding: "12px 24px" }}>
							<Button
								onClick={() => {
									setDeleteTarget(null);
									setDeleteForce(false);
								}}
								disabled={deleting}>
								Cancel
							</Button>
							<Button
								onClick={handleDelete}
								style={{
									color: "#fff",
									background: "#dc2626",
									borderRadius: 8,
									textTransform: "none",
									fontWeight: 600,
								}}
								variant="contained"
								disabled={deleting}>
								{deleting ? "Deleting..." : deleteForce ? "Force Delete" : "Delete"}
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</PageWrap>
	);
}
